package fr.manyverse.app.domain.image

/**
 * Single source of truth for the image formats accepted by the upload pipeline.
 *
 * Every platform image picker MUST yield an [fr.manyverse.app.domain.model.ImageData]
 * whose `mimeType` is one of [mimeTypes]: desktop/web restrict the selection dialog,
 * mobile converts unsupported formats (e.g. HEIC) to JPEG at the source.
 * [fr.manyverse.app.domain.validation.ImageValidator] enforces the same contract.
 */
object SupportedImageFormats {
    val mimeTypes: Set<String> = setOf("image/jpeg", "image/png", "image/webp")

    /** File extensions matching [mimeTypes], for native file-dialog filters. */
    val fileExtensions: List<String> = listOf("jpg", "jpeg", "png", "webp")

    /** Value for an HTML `<input accept="...">` attribute. */
    val acceptAttribute: String = mimeTypes.joinToString(",")

    fun isSupported(mimeType: String): Boolean = mimeType in mimeTypes

    /** File extension for a supported MIME type. Falls back to "jpg" for anything else. */
    fun extensionFor(mimeType: String): String = when (mimeType) {
        "image/jpeg" -> "jpg"
        "image/png" -> "png"
        "image/webp" -> "webp"
        else -> "jpg"
    }

    /** MIME type for a file extension. Falls back to "image/jpeg" for anything else. */
    fun mimeFor(extension: String): String = when (extension.lowercase()) {
        "jpg", "jpeg" -> "image/jpeg"
        "png" -> "image/png"
        "webp" -> "image/webp"
        else -> "image/jpeg"
    }
}
